/**
 * OVERVOLT entity shapes and factories.
 *
 * Entities are plain mutable records (no ECS — the counts are small). The
 * simulation mutates them; the renderer and the test snapshot only read them.
 */
import { CONFIG, type EnemyType, type SpawnType, type TargetMode, type TowerType } from '../config';
import type { Cell } from './map';

/**
 * Render radius (px) of the Kernel boss — it draws ≈1.6 tiles wide (docs §17.1
 * / §11). Render-only; the boss occupies a single tile for pathing.
 */
export const BOSS_RADIUS = 30;

/** A timed slow effect — see docs/GAME_DESIGN.md §5 Status Effects. */
export interface SlowStatus {
  /** Speed multiplier in (0, 1]; the enemy moves at `baseSpeed × factor`. */
  factor: number;
  /** Logical tick at which the slow expires (cleared when `tick ≥ this`). */
  expiresTick: number;
}

/** A stacking armor-shred effect — see docs/GAME_DESIGN.md §5 Status Effects. */
export interface CorrodeStatus {
  /** Active stacks (1..corrodeMaxStacks); each shreds `corrodeArmorPerStack`. */
  stacks: number;
  /** Logical tick at which all stacks drop at once. */
  expiresTick: number;
}

/**
 * A damage-vulnerability mark applied by the Cryo Emitter (the chokepoint
 * painter — docs/GAME_DESIGN.md §5). While active, every incoming damage
 * instance is scaled by `amp` (a multiplier ≥ 1.0) **before** armor / shield
 * resolution, so the uplift applies uniformly across single-target, AoE,
 * pierce, shield and boss damage alike. Refresh-on-reapply (never compounds):
 * the strongest amp wins (`max`) and the timer always refreshes.
 */
export interface BrittleStatus {
  /** Damage multiplier in `[1.0, +∞)`; `1.0` would be inert. */
  amp: number;
  /** Logical tick at which the mark expires (cleared when `tick ≥ this`). */
  expiresTick: number;
}

/**
 * A hard-stun EMP effect — see docs/GAME_DESIGN.md §5 Status Effects. While
 * active the enemy is fully stopped (speed 0) and its special action (Patch
 * heal, Buffer regen) is suppressed. Binary — it does not stack.
 */
export interface EmpStatus {
  /** Logical tick at which the stun expires (cleared when `tick ≥ this`). */
  expiresTick: number;
}

/**
 * Generic status-effect bag carried by every enemy — one slot per effect type
 * so further effects plug in without rework.
 */
export interface EnemyStatuses {
  /** Active slow effect, or null. */
  slow: SlowStatus | null;
  /** Active corrosion (armor shred), or null. */
  corrode: CorrodeStatus | null;
  /** Active brittle damage-vulnerability mark (Cryo painter, §5), or null. */
  brittle: BrittleStatus | null;
  /** Active EMP stun, or null. */
  emp: EmpStatus | null;
}

/** A marching Glitch. */
export interface Enemy {
  id: number;
  /** Spawn type — a roster archetype, or `'kernel'` for the Kernel boss (§17). */
  type: SpawnType;
  /** Current hit points. */
  hp: number;
  /** Hit points at spawn (after per-wave scaling) — for HP bars. */
  maxHp: number;
  /** Current shield HP — absorbs damage before `hp` (0 for non-Buffer types). */
  shield: number;
  /** Shield pool at spawn (after per-wave scaling); 0 for non-Buffer types. */
  maxShield: number;
  /** Ticks remaining before the shield may regenerate (0 = regen allowed). */
  shieldRegenCooldown: number;
  /**
   * The enemy's own route — a tile polyline from its origin to the Core. On a
   * fixed-path map every enemy shares the global `state.path`; in Maze Mode
   * each enemy carries an independent A*-computed route (docs §16.6).
   */
  path: readonly Cell[];
  /** Distance travelled along `path`, in tiles. */
  pathDist: number;
  /** `pathDist` at the start of the current tick (for render interpolation). */
  prevPathDist: number;
  /** Remaining distance to the Core along `path`, in tiles (`pathLen − pathDist`). */
  distToCore: number;
  /** The grid tile the enemy currently occupies (nearest tile on `path`). */
  tile: Cell;
  /** Base travel speed in tiles per second (before status effects). */
  speed: number;
  /** Gold + score granted on kill. */
  bounty: number;
  /** Lives drained from the Core on leak. */
  lifeDmg: number;
  /** Base flat per-hit damage reduction, before Corrosion shred. */
  baseArmor: number;
  /** Effective flat per-hit damage reduction after Corrosion shred. */
  armor: number;
  /** Ticks since this enemy last took damage (drives the hit flash). */
  hitFlash: number;
  /** Active status effects (slow, …). */
  statuses: EnemyStatuses;
  /** Id of the Fork (or Kernel) that spawned this enemy, or null if spawned directly. */
  parentId: number | null;
  /** Kernel boss phase (1–3, see §17.2); `0` on every non-boss enemy. */
  phase: number;
  /**
   * Streak lone-wolf sprint indicator (docs/ENEMY_DESIGN.md §3.2 (b)) —
   * `true` when this Streak's last `moveEnemies` tick saw no other living
   * enemy inside `streakSprintRadius`, so its effective speed was multiplied
   * by `streakSprintMult`. Always `false` for every non-Streak type (the AI
   * never sets it). Recomputed every tick — render-and-test aid only,
   * never read by the sim.
   */
  isSprinting: boolean;
  alive: boolean;
}

/** A placed tower. */
export interface Tower {
  id: number;
  type: TowerType;
  col: number;
  row: number;
  /** Upgrade level, 1–3. */
  level: number;
  /** Range in tiles (0 for the Pylon) — resolved for the current level. */
  range: number;
  /**
   * Splash Mortar dead-zone minimum range in tiles — resolved for the current
   * level; `0` for every other tower. The Mortar cannot target an enemy
   * closer than this, only one in the annulus between `minRange` and `range`
   * (docs/GAME_DESIGN.md §5).
   */
  minRange: number;
  /** Damage per shot — resolved for the current level. */
  damage: number;
  /** Base cooldown in ticks — resolved for the current level. */
  cooldownTicks: number;
  /** Ticks remaining until this tower may fire again. */
  cooldownLeft: number;
  /** Power demand drawn from the grid — resolved for the current level. */
  powerDraw: number;
  /** Power supply contributed to the grid — resolved (0 for weapon towers). */
  supply: number;
  /** Splash Mortar blast radius in tiles — resolved (0 for other towers). */
  blastRadius: number;
  /** Total gold sunk into this tile (build + upgrades; drives the refund). */
  totalSpent: number;
  /** Gold for the next upgrade, or null at max level. */
  upgradeCost: number | null;
  /** Id of the current primary target, or -1 when idle. */
  targetId: number;
  /** Player-selectable target-selection mode (weapon towers; `first` on Pylon). */
  targetMode: TargetMode;
  /**
   * Per-tower **OVERCLOCK** toggle (REC 3, Sprint 26 — docs/REC3_DESIGN.md
   * §4). While true the tower's effective `powerDraw` is multiplied by
   * `CONFIG.overclockDrawMult` (rounded) and its post-efficiency cooldown is
   * divided by `CONFIG.overclockRateMult` — bursty +DPS at the cost of a
   * grid-wide demand spike. Always `false` on the Pylon and Wall (no fire
   * loop / no power loop). Defaults `false`; flipped from the contextual
   * selection panel (`O` hotkey) or the `toggleOverclock` / `setOverclock`
   * test hooks. Pure boolean; sell / re-build resets it; upgrade preserves it.
   */
  overclock: boolean;
  /**
   * Cached **Pylon-aura** fire-rate multiplier (REC 3.b, Sprint 39 —
   * docs/REC3_DESIGN.md §2.B). Derived value, recomputed by
   * `applyPylonAuras` whenever the Pylon roster changes (place / sell /
   * upgrade). `1 + min(stackCap, n) × pylonAuraRateBuff` where `n` is the
   * count of Pylons inside `pylonAuraRadius × tileSize` of this tower's
   * centre. `1` (inert) on Pylons / Walls and on any weapon tower with no
   * Pylons in radius — so a no-aura build sees the byte-identical Sprint-26
   * cooldown. Read once per fire in `applyCooldown`.
   */
  auraRateMult: number;
}

/**
 * What spawned a beam effect — one of the six tower types, or `'airstrike'`
 * for the Airstrike ability's shell. Drives the beam color and VFX dispatch.
 */
export type ProjectileKind = TowerType | 'airstrike';

/** A short-lived hitscan beam, kept purely for rendering and test assertions. */
export interface Projectile {
  id: number;
  /** Beam origin, playfield-local pixels. */
  from: { x: number; y: number };
  /** Beam endpoint, playfield-local pixels. */
  to: { x: number; y: number };
  /** Ticks of life remaining. */
  ttlTicks: number;
  /** What fired it — a tower type or the Airstrike ability — drives the visual. */
  kind: ProjectileKind;
}

/** A kill-burst particle (visual only, deterministic via the seeded RNG). */
export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  ttlTicks: number;
  maxTtl: number;
  color: string;
}

/**
 * Create an enemy of `type` for `wave`, applying the compounding per-wave HP
 * curve and the combined `hpMult` — the difficulty `enemyHpMult` times the
 * wave's `overloadHpMult` (see docs/GAME_DESIGN.md §5/§8). `path` is the route
 * the enemy will walk (the global `state.path`, or a fresh A* route in Maze
 * Mode); `parentId` links a Fork child to the Fork that spawned it.
 */
export function createEnemy(
  id: number,
  type: EnemyType,
  wave: number,
  hpMult: number,
  path: readonly Cell[],
  parentId: number | null = null,
): Enemy {
  const def = CONFIG.enemies[type];
  const scale = hpMultiplier(wave) * hpMult;
  const maxHp = Math.round(def.hp * scale);
  // The Buffer's shield pool scales identically to HP; 0 for every other type.
  const maxShield = Math.round(def.baseShield * scale);
  return {
    id,
    type,
    hp: maxHp,
    maxHp,
    shield: maxShield,
    maxShield,
    shieldRegenCooldown: 0,
    path,
    pathDist: 0,
    prevPathDist: 0,
    distToCore: Math.max(0, path.length - 1),
    tile: path[0] ?? { col: 0, row: 0 },
    speed: def.speed,
    bounty: def.bounty,
    lifeDmg: def.lifeDmg,
    baseArmor: def.baseArmor,
    armor: def.baseArmor,
    hitFlash: 0,
    statuses: { slow: null, corrode: null, brittle: null, emp: null },
    parentId,
    phase: 0,
    isSprinting: false,
    alive: true,
  };
}

/**
 * Create the Kernel boss for `wave` — see docs/GAME_DESIGN.md §17. Its
 * effective HP rides the §8 compounding curve and the combined `hpMult` (the
 * difficulty `enemyHpMult` × the wave's `overloadHpMult`), but — unlike every
 * other enemy — the per-wave `hpMultiplier(w)` factor is clamped at
 * `bossHpWaveMultCap` (≈ its wave-15 value, §17.4) so an Endless-milestone
 * Kernel stays a hard-but-killable fight rather than an unkillable survival
 * wall. It carries no shield, `bossArmor` (0) armor, and starts in phase 1
 * (Boot Sequence) at `bossPhaseSpeed[0]`. `path` is the route it will walk
 * (the global `state.path`, or a fresh A* route in Maze Mode).
 */
export function createBoss(
  id: number,
  wave: number,
  hpMult: number,
  path: readonly Cell[],
): Enemy {
  const waveMult = Math.min(hpMultiplier(wave), CONFIG.bossHpWaveMultCap);
  const maxHp = Math.round(CONFIG.bossBaseHp * waveMult * hpMult);
  return {
    id,
    type: 'kernel',
    hp: maxHp,
    maxHp,
    shield: 0,
    maxShield: 0,
    shieldRegenCooldown: 0,
    path,
    pathDist: 0,
    prevPathDist: 0,
    distToCore: Math.max(0, path.length - 1),
    tile: path[0] ?? { col: 0, row: 0 },
    speed: CONFIG.bossPhaseSpeed[0],
    bounty: CONFIG.bossBounty,
    lifeDmg: CONFIG.bossLifeDmg,
    baseArmor: CONFIG.bossArmor,
    armor: CONFIG.bossArmor,
    hitFlash: 0,
    statuses: { slow: null, corrode: null, brittle: null, emp: null },
    parentId: null,
    phase: 1,
    isSprinting: false,
    alive: true,
  };
}

/** Display colour for any spawn type — a roster archetype, or the Kernel boss. */
export function enemyColor(type: SpawnType): string {
  return type === 'kernel' ? CONFIG.colors.kernel : CONFIG.enemies[type].color;
}

/** Render radius (px) for any spawn type — a roster archetype, or the Kernel boss. */
export function enemyRadius(type: SpawnType): number {
  return type === 'kernel' ? BOSS_RADIUS : CONFIG.enemies[type].radius;
}

/**
 * Recompute an enemy's derived track fields — `distToCore` (remaining tiles to
 * the Core) and `tile` (the nearest tile on its route) — from its current
 * `path` and `pathDist`. Call after movement or a Maze-mode re-path (§16.6).
 */
export function refreshEnemyTrack(enemy: Enemy): void {
  const maxIdx = enemy.path.length - 1;
  enemy.distToCore = maxIdx - enemy.pathDist;
  let i = Math.round(enemy.pathDist);
  if (i < 0) i = 0;
  else if (i > maxIdx) i = maxIdx;
  enemy.tile = enemy.path[i] ?? enemy.tile;
}

/**
 * Compounding per-wave HP growth factor — see docs/GAME_DESIGN.md §8. The
 * quadratic term keeps enemy toughness ahead of multiplicative player power
 * growth, so a finished board can no longer coast the final waves.
 */
export function hpMultiplier(wave: number): number {
  const w = wave - 1;
  return 1 + CONFIG.hpGrowthLinear * w + CONFIG.hpGrowthQuad * w * w;
}

/**
 * Late-wave enemy speed ramp — see docs/GAME_DESIGN.md §8. Balance-neutral
 * (1.0) up to `lateSpeedRampStart`, then climbs `lateSpeedRampPerWave` per
 * wave, capped at `lateSpeedRampMax` so Endless stays bounded.
 */
export function waveSpeedRamp(wave: number): number {
  const beyond = Math.max(0, wave - CONFIG.lateSpeedRampStart);
  return Math.min(CONFIG.lateSpeedRampMax, 1 + CONFIG.lateSpeedRampPerWave * beyond);
}

/**
 * Resolve a tower's level-dependent stats (range / damage / cooldown / power
 * draw / supply / next upgrade cost) from config into the entity. Call after
 * `level` changes. `level` is an invariant 1..`maxTowerLevel`, so the level
 * and upgrade-cost lookups are provably in range.
 */
export function applyTowerLevel(tower: Tower): void {
  const def = CONFIG.towers[tower.type];
  const lvl = def.levels[tower.level - 1]!;
  tower.range = lvl.range;
  tower.damage = lvl.damage;
  tower.cooldownTicks = lvl.cooldownTicks;
  tower.powerDraw = lvl.powerDraw;
  // Supply is resolved onto the entity (like powerDraw) so a placed Pylon is
  // immune to later config mutation — symmetric with weapon towers.
  tower.supply = tower.type === 'pylon' ? CONFIG.towers.pylon.levels[tower.level - 1]!.supply : 0;
  tower.blastRadius =
    tower.type === 'mortar' ? CONFIG.towers.mortar.levels[tower.level - 1]!.blastRadius : 0;
  // The Mortar's dead-zone minimum range — resolved like blastRadius; 0 for
  // every other tower, so the shared target-selection filter is a no-op there.
  tower.minRange =
    tower.type === 'mortar' ? CONFIG.towers.mortar.levels[tower.level - 1]!.minRange : 0;
  // The Wall is never upgradable (`upgradable: false`), so it always exposes a
  // null upgrade cost — the contextual panel then shows no Upgrade row (§16.9).
  tower.upgradeCost =
    def.upgradable !== false && tower.level < CONFIG.maxTowerLevel
      ? def.upgradeCosts[tower.level - 1]!
      : null;
}

/** Create a level-1 tower of `type` at the given tile. */
export function createTower(id: number, type: TowerType, col: number, row: number): Tower {
  const tower: Tower = {
    id,
    type,
    col,
    row,
    level: 1,
    range: 0,
    minRange: 0,
    damage: 0,
    cooldownTicks: 0,
    cooldownLeft: 0,
    powerDraw: 0,
    supply: 0,
    blastRadius: 0,
    totalSpent: CONFIG.towers[type].cost,
    upgradeCost: null,
    targetId: -1,
    targetMode: CONFIG.towers[type].defaultTargetMode ?? 'first',
    // Sprint 26 (REC 3): per-tower Overclock starts OFF on every fresh build,
    // so a no-Overclock player sees bit-for-bit Sprint-25 behaviour.
    overclock: false,
    // Sprint 39 (REC 3.b): aura multiplier is inert (1.0) until a Pylon is
    // placed inside the new tower's aura radius — `applyPylonAuras` rewrites
    // it whenever the Pylon roster changes (place / sell / upgrade).
    auraRateMult: 1,
  };
  applyTowerLevel(tower);
  return tower;
}
